import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { GitCompare, Trophy, Landmark, AlertTriangle, Search, ArrowUpDown, Target, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { useStatusFilter } from "@/context/StatusFilterContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PdfToolbar } from "@/components/PdfToolbar";
import { useAuth } from "@/context/AuthContext";
import { isGeneralManager } from "@/lib/roles";

const A = "#06B6D4", B = "#8B5CF6";
const GREEN = "#16A34A", RED = "#DC2626", AMBER = "#D97706", NAVY = "#0F2C59";
const axis = { fontSize: 11, fill: "#94a3b8" };
const grid = "#1e293b";
const tip = { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 };

function merge(wa, wb) {
  const map = {};
  (wa || []).forEach((w) => { map[w.label] = { label: w.label, ...map[w.label], a_bize: w.bize, a_karsi: w.karsi, a_field: w.field, a_work: w.work_score }; });
  (wb || []).forEach((w) => { map[w.label] = { label: w.label, ...map[w.label], b_bize: w.bize, b_karsi: w.karsi, b_field: w.field, b_work: w.work_score }; });
  return Object.values(map);
}

function ChartCard({ title, subtitle, children, testid, tall }) {
  return (
    <Card className="p-4 border border-border pdf-section" data-testid={testid}>
      <h3 className="font-display font-semibold text-sm">{title}</h3>
      {subtitle && <p className="text-[11px] text-muted-foreground mb-1">{subtitle}</p>}
      <div className={`${tall ? "h-80" : "h-60"} w-full mt-1`}>{children}</div>
    </Card>
  );
}

function Kpi({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <Card className="p-3.5 flex items-center gap-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}1a`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold leading-tight" style={{ color }}>{value}</div>
        <div className="text-[11px] text-muted-foreground truncate">{label}</div>
        {sub && <div className="text-[10px] text-muted-foreground/80 truncate">{sub}</div>}
      </div>
    </Card>
  );
}

const statusOf = (r) => {
  if (r.margin > 10) return { label: "Güvenli", color: GREEN };
  if (r.margin > 0) return { label: "Dar Farkla Önde", color: AMBER };
  if (r.missing <= 5) return { label: "Kritik", color: AMBER };
  return { label: "Kaybediyor", color: RED };
};

function AllCommittees({ mode, setMode }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("risk");

  useEffect(() => {
    api.get("/dashboard/committee-scorecard").then(({ data }) => setRows(data.rows || [])).catch(() => setRows([]));
  }, []);

  const list = useMemo(() => {
    if (!rows) return [];
    return rows.map((r) => {
      const m = (mode === "field" ? r.field : r.forecast) || {};
      const bize = m.bize || 0, karsi = m.karsi || 0, target = m.win_target || 0;
      return {
        committee_no: r.committee_no, name: r.name, seats: r.council_seats || 0,
        faal: r.faal || 0, contacted: r.contacted || 0, health: r.health || 0,
        belge: r.belge || 0, belge_target: r.belge_target || 0, belge_pct: r.belge_pct || 0,
        bize, karsi, target, turnout: m.turnout || 0,
        margin: bize - karsi,
        missing: Math.max(target - bize, 0),
        winning: !!m.winning,
        reach_pct: r.faal ? Math.round((r.contacted || 0) / r.faal * 100) : 0,
      };
    });
  }, [rows, mode]);

  const totals = useMemo(() => {
    const t = { seats: 0, wonSeats: 0, won: 0, bize: 0, karsi: 0, faal: 0, contacted: 0, health: 0, missing: 0 };
    list.forEach((r) => {
      t.seats += r.seats; t.bize += r.bize; t.karsi += r.karsi;
      t.faal += r.faal; t.contacted += r.contacted; t.health += r.health;
      if (r.winning) { t.won += 1; t.wonSeats += r.seats; } else { t.missing += r.missing; }
    });
    t.avgHealth = list.length ? Math.round(t.health / list.length) : 0;
    t.majority = Math.floor(t.seats / 2) + 1;
    t.reach = t.faal ? Math.round(t.contacted / t.faal * 100) : 0;
    return t;
  }, [list]);

  const shown = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    let out = s
      ? list.filter((r) => String(r.committee_no).includes(s) || r.name.toLocaleLowerCase("tr").includes(s))
      : [...list];
    const cmp = {
      risk: (a, b) => a.margin - b.margin,
      no: (a, b) => a.committee_no - b.committee_no,
      seats: (a, b) => b.seats - a.seats,
      health: (a, b) => a.health - b.health,
      missing: (a, b) => b.missing - a.missing,
      margin: (a, b) => b.margin - a.margin,
    }[sort];
    return out.sort(cmp);
  }, [list, q, sort]);
  const cT = useTableSort(shown);
  const planT = useTableSort([]);

  const riskChart = useMemo(
    () => [...list].sort((a, b) => a.margin - b.margin).slice(0, 12)
      .map((r) => ({ label: `#${r.committee_no}`, fark: r.margin, name: r.name })),
    [list]
  );
  const seatPie = useMemo(() => [
    { name: "Kazanılan koltuk", value: totals.wonSeats, fill: GREEN },
    { name: "Kaybedilen koltuk", value: Math.max(totals.seats - totals.wonSeats, 0), fill: RED },
  ], [totals]);
  const voteChart = useMemo(
    () => [...list].sort((a, b) => b.faal - a.faal).slice(0, 15)
      .map((r) => ({ label: `#${r.committee_no}`, Bizim: r.bize, Karşı: r.karsi })),
    [list]
  );
  const healthBands = useMemo(() => {
    const bands = [
      { label: "0-40 Riskli", color: RED, min: 0, max: 40 },
      { label: "40-60 Orta", color: AMBER, min: 40, max: 60 },
      { label: "60-80 İyi", color: "#0EA5E9", min: 60, max: 80 },
      { label: "80-100 Güçlü", color: GREEN, min: 80, max: 101 },
    ];
    return bands.map((b) => ({ label: b.label, adet: list.filter((r) => r.health >= b.min && r.health < b.max).length, fill: b.color }));
  }, [list]);

  // Çoğunluk planı: koltuk başına en az oy gerektiren komiteler
  const plan = useMemo(() => {
    const sorted = [...list].sort((a, b) => {
      if (a.winning !== b.winning) return a.winning ? -1 : 1;
      const pa = a.seats ? a.missing / a.seats : Infinity;
      const pb = b.seats ? b.missing / b.seats : Infinity;
      return pa - pb;
    });
    let seats = 0, votes = 0;
    const steps = [];
    for (const r of sorted) {
      if (seats >= totals.majority) break;
      seats += r.seats; votes += r.missing;
      if (!r.winning) steps.push({ ...r, cumSeats: seats, cumVotes: votes });
    }
    return { seats, votes, steps: steps.slice(0, 8) };
  }, [list, totals.majority]);

  if (!rows) return <Skeleton className="h-72 w-full rounded-lg" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid="compare-mode-metric">
          {[["field", "Saha (Güvence)"], ["forecast", "2026 Tahmini"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`compare-metric-${k}`}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${mode === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {mode === "field" ? "Saha: kesin oy + yetki belgesi güvencesi" : "2026 Tahmini: geçmiş katılım × mevcut eğilim"}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi icon={Trophy} label="Kazanılan komite" value={`${totals.won}/${list.length}`} color={GREEN} testid="kpi-won" />
        <Kpi icon={Landmark} label="Kazanılan meclis üyeliği" value={`${totals.wonSeats}/${totals.seats}`} sub={`Çoğunluk: ${totals.majority}`} color={NAVY} testid="kpi-seats" />
        <Kpi icon={Target} label="Çoğunluğa kalan koltuk" value={Math.max(totals.majority - totals.wonSeats, 0)} sub={plan.seats >= totals.majority ? `+${plan.votes} oy ile ulaşılır` : "Mevcut veriyle zor"} color={AMBER} testid="kpi-majority-gap" />
        <Kpi icon={Users2} label="Bizim oy" value={totals.bize} color={GREEN} testid="kpi-bize" />
        <Kpi icon={AlertTriangle} label="Karşı oy" value={totals.karsi} color={RED} testid="kpi-karsi" />
        <Kpi icon={GitCompare} label="Net fark" value={`${totals.bize - totals.karsi > 0 ? "+" : ""}${totals.bize - totals.karsi}`} color={totals.bize >= totals.karsi ? GREEN : RED} testid="kpi-margin" />
        <Kpi icon={Search} label="Ulaşılan" value={`%${totals.reach}`} sub={`Ort. sağlık: ${totals.avgHealth}`} color="#0EA5E9" testid="kpi-reach" />
      </div>

      <Card className="p-4 border border-border" data-testid="compare-majority-plan">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h3 className="font-display font-semibold text-sm">Çoğunluk Yol Haritası — {totals.majority} meclis üyeliği</h3>
          <span className="text-xs text-muted-foreground">
            Şu an <b style={{ color: GREEN }}>{totals.wonSeats}</b> koltuk · en verimli komitelerde <b style={{ color: AMBER }}>+{plan.votes}</b> oy ile <b>{plan.seats}</b> koltuk
          </span>
        </div>
        <Progress value={Math.min(totals.wonSeats / totals.majority * 100, 100)} className="h-2.5" />
        {plan.steps.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-muted-foreground border-b border-border">
                <planT.Th sortKey="pl-komite" get={(r) => r.name} text className="text-left py-1.5">Öncelikli komite</planT.Th><planT.Th sortKey="pl-koltuk" get={(r) => r.seats} className="text-center">Koltuk</planT.Th>
                <planT.Th sortKey="pl-eksik" get={(r) => r.missing} lowBest className="text-center">Eksik oy</planT.Th><planT.Th sortKey="pl-basi" get={(r) => (r.seats ? r.missing / r.seats : 0)} lowBest className="text-center">Koltuk başı oy</planT.Th><planT.Th sortKey="pl-kumulatif" get={(r) => r.cum_seats ?? 0} className="text-center">Kümülatif koltuk</planT.Th>
              </tr></thead>
              <tbody>
                {planT.applySort(plan.steps).map((s) => (
                  <tr key={s.committee_no} className="border-b border-border/40" data-testid={`plan-row-${s.committee_no}`}>
                    <td className="py-1.5">#{s.committee_no} {s.name}</td>
                    <td className="text-center tabular-nums">{s.seats}</td>
                    <td className="text-center tabular-nums font-semibold" style={{ color: AMBER }}>+{s.missing}</td>
                    <td className="text-center tabular-nums">{s.seats ? (s.missing / s.seats).toFixed(1) : "—"}</td>
                    <td className="text-center tabular-nums">{s.cumSeats}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="En Riskli 12 Komite" subtitle="Bizim oy − karşı oy farkı (negatif = geride)" testid="chart-risk">
          <ResponsiveContainer>
            <BarChart data={riskChart} layout="vertical" margin={{ left: 4, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis type="number" tick={axis} />
              <YAxis type="category" dataKey="label" tick={axis} width={40} />
              <Tooltip contentStyle={tip} formatter={(v, n, p) => [v, p.payload.name]} />
              <Bar dataKey="fark" radius={[0, 4, 4, 0]}>
                {riskChart.map((r, i) => <Cell key={i} fill={r.fark < 0 ? RED : r.fark <= 10 ? AMBER : GREEN} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Meclis Üyeliği Dağılımı" subtitle={`${totals.seats} üyelik · çoğunluk ${totals.majority}`} testid="chart-seats">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={seatPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3} label />
              <Tooltip contentStyle={tip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Bizim vs Karşı — En Büyük 15 Komite" testid="chart-votes">
          <ResponsiveContainer>
            <BarChart data={voteChart}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="label" tick={axis} /><YAxis tick={axis} />
              <Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Bizim" fill={GREEN} radius={[4, 4, 0, 0]} />
              <Bar dataKey="Karşı" fill={RED} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sağlık Skoru Dağılımı" subtitle="Komite sayısı" testid="chart-health">
          <ResponsiveContainer>
            <BarChart data={healthBands}>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="label" tick={{ ...axis, fontSize: 10 }} /><YAxis tick={axis} allowDecimals={false} />
              <Tooltip contentStyle={tip} />
              <Bar dataKey="adet" radius={[4, 4, 0, 0]}>
                {healthBands.map((b, i) => <Cell key={i} fill={b.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <Card className="p-4 border border-border pdf-section" data-testid="compare-all-table">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display font-semibold text-sm">Tüm Komiteler — Kazanma Tablosu</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Komite ara" className="h-8 pl-8 text-xs w-44" data-testid="compare-all-search" />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="compare-all-sort">
                <ArrowUpDown className="h-3.5 w-3.5 mr-1" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="risk">En riskli önce</SelectItem>
                <SelectItem value="missing">En çok eksik oy</SelectItem>
                <SelectItem value="margin">En yüksek fark</SelectItem>
                <SelectItem value="seats">Koltuk sayısı</SelectItem>
                <SelectItem value="health">En düşük sağlık</SelectItem>
                <SelectItem value="no">Komite no</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs md:text-sm">
            <thead>
              <tr className="text-muted-foreground text-[11px] border-b border-border">
                <cT.Th sortKey="c-komite" get={(r) => r.name} text className="text-left py-2">Komite</cT.Th>
                <cT.Th sortKey="c-koltuk" get={(r) => r.council_seats ?? 0} className="text-center">Koltuk</cT.Th>
                <cT.Th sortKey="c-faal" get={(r) => r.faal} className="text-center">Faal</cT.Th>
                <cT.Th sortKey="c-ulasilan" get={(r) => r.contacted} className="text-center">Ulaşılan</cT.Th>
                <cT.Th sortKey="c-bizim" get={(r) => r.bize} className="text-center">Bizim</cT.Th>
                <cT.Th sortKey="c-karsi" get={(r) => r.karsi} lowBest className="text-center">Karşı</cT.Th>
                <cT.Th sortKey="c-fark" get={(r) => r.margin} className="text-center">Fark</cT.Th>
                <cT.Th sortKey="c-hedef" get={(r) => r.win_target} className="text-center">Hedef</cT.Th>
                <cT.Th sortKey="c-eksik" get={(r) => r.gap ?? Math.max((r.win_target || 0) - (r.bize || 0), 0)} lowBest className="text-center">Eksik</cT.Th>
                <cT.Th sortKey="c-belge" get={(r) => r.belge ?? 0} className="text-center">Belge</cT.Th>
                <cT.Th sortKey="c-saglik" get={(r) => r.health} className="text-center">Sağlık</cT.Th>
                <cT.Th sortKey="c-durum" get={(r) => (r.winning ? 1 : 0)} className="text-center">Durum</cT.Th>
              </tr>
            </thead>
            <tbody>
              {cT.sorted.map((r) => {
                const st = statusOf(r);
                return (
                  <tr key={r.committee_no} className="border-b border-border/40 hover:bg-muted/40" data-testid={`compare-all-row-${r.committee_no}`}>
                    <td className="py-2"><b>#{r.committee_no}</b> <span className="text-muted-foreground">{r.name}</span></td>
                    <td className="text-center tabular-nums">{r.seats}</td>
                    <td className="text-center tabular-nums">{r.faal}</td>
                    <td className="text-center tabular-nums">%{r.reach_pct}</td>
                    <td className="text-center tabular-nums font-semibold" style={{ color: GREEN }}>{r.bize}</td>
                    <td className="text-center tabular-nums" style={{ color: RED }}>{r.karsi}</td>
                    <td className="text-center tabular-nums font-semibold" style={{ color: r.margin >= 0 ? GREEN : RED }}>{r.margin > 0 ? "+" : ""}{r.margin}</td>
                    <td className="text-center tabular-nums text-muted-foreground">{r.target}</td>
                    <td className="text-center tabular-nums font-semibold" style={{ color: r.missing ? AMBER : GREEN }}>{r.missing || "—"}</td>
                    <td className="text-center tabular-nums text-muted-foreground">{r.belge}/{r.belge_target}</td>
                    <td className="text-center tabular-nums">{r.health}</td>
                    <td className="text-center">
                      <span className="text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: `${st.color}1a`, color: st.color }}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={12} className="text-center py-6 text-muted-foreground">Eşleşen komite yok</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default function Compare() {
  const { status } = useStatusFilter();
  const { user } = useAuth();
  const isGM = isGeneralManager(user);
  const [view, setView] = useState("all");
  const [mode, setMode] = useState("field");
  const [committees, setCommittees] = useState([]);
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [data, setData] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/committees").then(({ data }) => {
      setCommittees(data);
      if (data.length >= 2) { setA(String(data[0].committee_no)); setB(String(data[1].committee_no)); }
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (view !== "pair" || !a || !b) return;
    api.get(`/analytics/compare?a=${a}&b=${b}&status=${status}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [view, a, b, status]);

  const rows = data ? [
    { l: "Kayıtlı", a: data.a.summary.registered, b: data.b.summary.registered, hi: "max" },
    { l: "Ulaşılan", a: data.a.summary.reached, b: data.b.summary.reached, hi: "max" },
    { l: "Bizim Oy", a: data.a.summary.bize, b: data.b.summary.bize, hi: "max" },
    { l: "Karşı Oy", a: data.a.summary.karsi, b: data.b.summary.karsi, hi: "min" },
    { l: "Oy Farkı", a: data.a.summary.bize - data.a.summary.karsi, b: data.b.summary.bize - data.b.summary.karsi, hi: "max" },
    { l: "Çalışma Skoru", a: data.a.summary.work_score, b: data.b.summary.work_score, hi: "max", pct: true },
    { l: "Saha %", a: data.a.summary.field_pct, b: data.b.summary.field_pct, hi: "max", pct: true },
  ] : [];
  const merged = data ? merge(data.a.weeks, data.b.weeks) : [];
  const nameA = data?.a.name, nameB = data?.b.name;
  const win = (r, side) => (r.hi === "max" ? (side === "a" ? r.a > r.b : r.b > r.a) : (side === "a" ? r.a < r.b : r.b < r.a));

  return (
    <div className="space-y-4" data-testid="compare-page" ref={ref}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><GitCompare className="h-6 w-6 text-cyan-500" /> Komite Analiz & Karşılaştırma</h1>
          <p className="text-sm text-muted-foreground">
            Seçimi kazandıracak tablo: tüm komiteler tek ekranda · Filtre: {status === "faal" ? "Faal" : status === "aski" ? "Askı" : "Tümü"}
          </p>
        </div>
        {isGM && <PdfToolbar targetRef={ref} filename="komite-analiz.pdf" title="Komite Analiz & Karşılaştırma" />}
      </div>

      <div className="inline-flex rounded-lg border border-border bg-muted p-0.5" data-testid="compare-view-tabs">
        {[["all", "Tüm Komiteler"], ["pair", "İkili Karşılaştırma"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`compare-view-${k}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${view === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {view === "all" ? (
        <AllCommittees mode={mode} setMode={setMode} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Select value={a || ""} onValueChange={setA}>
              <SelectTrigger data-testid="compare-select-a"><SelectValue placeholder="Komite A" /></SelectTrigger>
              <SelectContent className="max-h-72">{committees.map((c) => <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} {c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={b || ""} onValueChange={setB}>
              <SelectTrigger data-testid="compare-select-b"><SelectValue placeholder="Komite B" /></SelectTrigger>
              <SelectContent className="max-h-72">{committees.map((c) => <SelectItem key={c.committee_no} value={String(c.committee_no)}>#{c.committee_no} {c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {data && (
            <>
              <Card className="p-4 border border-border pdf-section overflow-x-auto" data-testid="compare-table">
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground text-xs border-b border-border"><th className="text-left py-2">Gösterge</th><th className="text-center py-2" style={{ color: A }}>{nameA}</th><th className="text-center py-2" style={{ color: B }}>{nameB}</th></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.l} className="border-b border-border/40">
                        <td className="py-2 text-muted-foreground">{r.l}</td>
                        <td className={`text-center py-2 font-semibold ${win(r, "a") ? "text-emerald-500 bg-emerald-500/10" : ""}`} data-testid={`compare-a-${r.l}`}>{r.pct ? `%${r.a}` : r.a}</td>
                        <td className={`text-center py-2 font-semibold ${win(r, "b") ? "text-emerald-500 bg-emerald-500/10" : ""}`} data-testid={`compare-b-${r.l}`}>{r.pct ? `%${r.b}` : r.b}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {[
                  { t: "Bizim Oy — Günlük", ka: "a_bize", kb: "b_bize" },
                  { t: "Karşı Oy — Günlük", ka: "a_karsi", kb: "b_karsi" },
                  { t: "Saha Teması — Günlük", ka: "a_field", kb: "b_field" },
                  { t: "Çalışma Skoru — Günlük", ka: "a_work", kb: "b_work" },
                ].map((ch) => (
                  <ChartCard key={ch.t} title={ch.t}>
                    <ResponsiveContainer>
                      <LineChart data={merged}>
                        <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis tick={axis} /><Tooltip contentStyle={tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey={ch.ka} name={nameA} stroke={A} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} /><Line type="monotone" dataKey={ch.kb} name={nameB} stroke={B} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartCard>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
